import { CanAccess, Authenticated } from "@refinedev/core";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { App as AntApp } from "antd";
import { RefineWrapper } from "@/components/RefineWrapper";
import { useEffect, useMemo } from "react";
import { myDataProvider } from "./providers/data-provider";
import routerProvider from "@refinedev/react-router-v6";
import {
  AppShellLayout,
  CustomSider,
  StudioHeader,
} from "components/layout";
import { hasLtiTabSession } from "@libertify/auth/core";
import { ColorModeContextProvider } from "contexts";
import { MobileCloningPage } from "./pages/media/contacts/MobileCloning";
import { authProvider, setupAuthProvider } from "./authProvider";
import Intercom, { shutdown } from "@intercom/messenger-js-sdk";
import ErrorBoundary from "components/ErrorBoundary";
import { LoadingSpinner } from "components/LoadingSpinner";
import HtmlPageTitle from "components/HtmlPageTitle";
import { initializeAnalytics, PageViewTracker } from "hooks/useAnalytics";
import { useAntTheme } from "hooks/useAntTheme";
import { useSession } from "hooks/useSession";
import { useMfaSetupNudge } from "hooks/useMfaSetupNudge";
import { EnableMfaToast } from "components/EnableMfaToast";
import { LiveActivitySubscription } from "components/liveActivity/LiveActivitySubscription";
import { CookieConsentProvider } from "./providers/CookieConsentProvider";
import { useTranslation } from "react-i18next";
import AssetLibraries from "pages/asset-libraries";
import CreateAssetLibraryPage from "pages/asset-libraries/create";
import ForgotPasswordPage from "pages/auth/forgot-password";
import { LoginPage } from "pages/auth/login";
import { RegisterPage } from "pages/auth/new-register";
import { EmailVerificationPage } from "pages/auth/verification";
import { BoursoramaVideoList } from "pages/extranet/boursorama";
import { MediaIndexPage } from "pages/media";
import { MediaAssetDetail } from "pages/media/assets/detail";
import { MediaAssetsList } from "pages/media/assets/list";
import { MediaAssetsReview } from "pages/media/assets/review";
import { MediaAssetsReviewStats } from "pages/media/assets/stats";
import { MediaBatchList } from "pages/media/batches/list";
import { ChangePassword } from "pages/auth/change-password";
import { MfaVerifyPage } from "pages/auth/mfa-verify";
import { AssetOverview } from "pages/assets/detail";
import { AssetChangesReview } from "pages/assets/changes";
import { AssetsEarningCalendarsPage } from "pages/assets/calendars";
import { ProjectDetail as ProjectDetailAll } from "pages/media/projects/detail-all";
import { Chat } from "pages/media/components/chat/Chat";
import { ContactListPage } from "pages/media/contacts";
import { ContactEditPage } from "pages/media/contacts/edit";
import CallToActionsListPage from "pages/media/ctas";
import { CallToActionsFormPage } from "pages/media/ctas/create";
import { MediaDashboardPage } from "pages/media/dashboard";
import { MediaDocumentCreate } from "pages/media/documents/create";
import { MediaDocumentDetail } from "pages/media/documents/detail";
import { MediaDocumentList } from "pages/media/documents/list";
import FaceProfilesListPage from "pages/media/faces";
import { FaceProfileCreatePage } from "pages/media/faces/create";
import FaceProfileDetailPage from "pages/media/faces/detail";
import { HomePage } from "pages/media/home";
import { BackgroundImage } from "pages/media/home/components/BackgroundImage";
import { ProjectInitPrompt } from "pages/media/home/components/ProjectInitPrompt/";
import { MediaCreate } from "pages/media/media/create";
import { MediaDetail } from "pages/media/media/detail";
import { MediaList } from "pages/media/media/list";
import { BrandKitList } from "pages/media/brandkit";
import { BrandKitEdit } from "pages/media/brandkit/edit";
import { BrandKitCreate } from "pages/media/brandkit/create";
import SystemPage from "pages/system/detail";
import PronunciationsPage from "pages/media/pronunciations";
import { OrganizationPreferencesPage } from "pages/media/organizations";
import OrganizationCreatePage from "pages/media/organizations/create";
import MediaOrganizationsList from "pages/media/organizations/list";
import PresenterProfilesListPage from "pages/media/presenters";
import ProductsList from "pages/media/products";
import ProductCreatePage from "pages/media/products/create";
import { ProjectsList } from "pages/media/projects";
import { OrganizationAnalyticsPage } from "pages/media/analytics";
import { AskDocs } from "pages/media/projects/AskDocs";
import { DownloadMediaPage } from "pages/media/projects/components/DownloadMediaPage";
import { ProjectCreate } from "pages/media/projects/create";
import { ProjectDocumentPage } from "pages/media/projects/document";
import { ProjectSelectType } from "pages/media/projects/select-type";
import { HeyGeniusProjectCreatePage } from "pages/media/projects/create/hey-genius";
import { MediaCuepointsAnimation } from "pages/media/projects/MediaCuepointsAnimation/";
import { Detail } from "pages/media/projects/detail/";
import { EditChat } from "pages/media/projects/editChat";
import { ProjectChatHistoryPage } from "pages/media/projects/chat-history";
import { ProjectKbPage } from "pages/media/projects/kb";
import { ProjectStepLoadingPage } from "pages/media/projects/loading";
import { ProjectSettingsPage } from "pages/media/projects/settings";

import { ProjectStoryboardEditor } from "pages/media/projects/storyboard";
import { HeyGeniusPreviewPage } from "pages/media/projects/heygenius-preview";
import { HeyGeniusInstallPage } from "pages/media/projects/heygenius-install";
import { SlidesStreaming } from "pages/media/projects/slides-streaming";
import { VoiceProfilesListPage } from "pages/media/voices";
import { VoiceProfileCreatePage } from "pages/media/voices/create";
import VoiceProfileDetailPage from "pages/media/voices/detail";
import OnboardingPage from "pages/onboarding";
import { OnboardingQuestionnaire } from "components/OnboardingQuestionnaire";
import PaywallPage from "pages/paywall";
import CreateOrganizationPage from "pages/onboarding/CreateOrganization";
import { UserEdit } from "pages/users/admin";
import { UserProfilePage } from "pages/users/edit";
import { TutorialsPage } from "pages/tutorials";
import { PlaybooksPage } from "pages/playbooks";
import { PlaybooksEditPage } from "pages/playbooks/edit";
import {
  ExperienceEditPage,
  ExperiencePage as ExperienceV2Page,
  ExperienceV2Layout,
} from "pages/experience-v2";
import {
  CreateExperiencePage,
  CreateExperienceLayout,
} from "pages/create-experience";
import {
  BrowserRouter,
  matchPath,
  Navigate,
  Outlet,
  Route,
  Routes,
  useLocation,
  useSearchParams,
} from "react-router-dom";
import { getSafeRedirectPath } from "@libertify/auth/refine-adapter";
import { ThemeProvider } from "styled-components";
import "./index.css";
import { Breadcrumbs } from "components/Breadcrumbs";
import SubscriptionPage from "pages/subscription";
import SubscriptionSuccess from "pages/subscription/success";
import SubscriptionDetail from "pages/subscription/detail/";
import SubscriptionCancel from "pages/subscription/cancel/";
import Subscriptions from "pages/subscription/subscription-plans/";
import Redirector from "components/Redirector";
import { AssetLibraryEdit } from "pages/asset-libraries/edit";
import { OAuthCallbackPage } from "pages/auth/OAuthCallback";
import { i18nProvider } from "./providers/i18nProvider";
import { AnalyticsDashboardPage } from "./pages/media/projects/analytics_new";
import { VideoExplainerAnalyticsPage } from "./pages/media/projects/analytics-video-explainer";
import { DocumentHighlighterAnalyticsPage } from "./pages/media/projects/analytics-document-highlighter";
import { AIChatbotAnalyticsPage } from "./pages/media/projects/analytics-ai-chatbot";
import { AddonsAnalyticsRoute } from "./pages/media/projects/analytics-addons";
import { ActivityAnalyticsPage } from "./pages/media/projects/analytics-activity";
import { ProjectProvider } from "./providers/ProjectProvider";
import UserInvitesPage from "./pages/users/invitations";

import { enableMapSet } from "immer";
import { TemplateDataProvider } from "./providers/TemplateDataProvider";
import { FlagPage } from "./pages/feature-flags";
import { FlagCreatePage } from "./pages/feature-flags/create";
import { FlagEditPage } from "./pages/feature-flags/edit";
import { WhatsNewPage } from "./pages/whats-new";
import {
  createAccessControlProvider,
  createHasRole,
} from "./config/accessControl";
import { getResources } from "./config/resources";
import { WorkspacesListPage } from "./pages/media/workspaces";
import { SubscriptionUsersPage } from "./pages/subscription/users";
import { ManageSubscriptionsPage } from "./pages/manage-subscriptions";
import {
  AuthInitializer,
  ExportPollingManager,
  NetworkManager,
  WebSocketManager,
} from "./managers";
import { WorkspacesDetailPage } from "./pages/media/workspaces/detail";
import { StoryboardEditor } from "./pages/media/components/storyboardEditor";
import { AvatarEdit } from "./pages/media/components/ContactWithPresenter/AvatarEdit";
import { ScalekitCallbackPage } from "./pages/auth/ScalekitCallback";
import LtiDeepLinkPage from "./pages/lti/LtiDeepLinkPage";
import LtiSessionExpiredPage from "./pages/lti/LtiSessionExpiredPage";
// TODO remove after testing
import TeacherOnboardingPage from "./pages/teacher-onboarding";
import { SsoLogoutPage } from "./pages/auth/SsoLogout";
import { ThemeEditorPage } from "./pages/media/components/themeEditor";
import { NotFoundPage } from "./pages/not-found/NotFoundPage";
import { SoftPaywallProvider } from "./providers/SoftPaywallProvider";

//set immer to use map
enableMapSet();

// to refactor with metadata (v6) in the future
// https://reactrouter.com/6.29.0/route/route
// const Route = ({ title, ...rest }: RouteProps & { title?: string }) => (
//   <React.Fragment>
//     {/* <PageTitle title={title} />  */}
//     <BaseRoute {...rest} />
//   </React.Fragment>
// );
const ScrollToTop = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    globalThis.scrollTo(0, 0);
  }, [pathname]);
  return null;
};

// Replaces an unsafe ?to= with /home before Refine's useLogin reads it.
const SafeRedirectParam = () => {
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const to = searchParams.get("to");

  if (to !== null && getSafeRedirectPath(to) !== to) {
    const safeParams = new URLSearchParams(searchParams);
    safeParams.set("to", getSafeRedirectPath(to));
    return (
      <Navigate
        to={{ pathname: location.pathname, search: `?${safeParams}` }}
        replace
      />
    );
  }
  return <Outlet />;
};
// Renders nothing in a layout slot (used to hide the sider, which also holds the user menu).
const EmptyLayoutSlot = () => null;

const Element = () => {
  const { theme } = useAntTheme();
  const { user, isAuthenticated, isLoading } = useSession();
  const location = useLocation();
  const isNewProjectPage = Boolean(
    matchPath({ path: "/media/projects/:id", end: true }, location.pathname)
  );
  // LTI (Moodle) tabs get project analytics and chat history without the sidebar and user menu.
  const isLtiAnalyticsPage =
    hasLtiTabSession() &&
    (Boolean(matchPath("/media/projects/:id/analytics/*", location.pathname)) ||
      Boolean(
        matchPath("/media/projects/:id/chat-history", location.pathname)
      ));
  const mfaSetupNudge = useMfaSetupNudge(user?.id, isAuthenticated);

  useEffect(() => {
    if (isLoading) {
      return; //avoid init intercom if too early
    }
    let userProps = {};
    if (user && isAuthenticated) {
      const { id, first_name, last_name, email, created } = user;
      userProps = {
        user_id: String(id),
        name: first_name + " " + last_name,
        email: email,
        created_at: created ? new Date(created).getTime() / 1000 : 0,
      };
    }
    Intercom({
      app_id: "mcwfcpeh",
      ...userProps,
      hide_default_launcher: isNewProjectPage,
    });

    if (isNewProjectPage) {
      globalThis?.Intercom?.("hide");
    }

    return () => {
      shutdown();
    };
  }, [user, isAuthenticated, isLoading, isNewProjectPage]);

  // Don't render layout until session data is loaded to prevent excessive re-renders (flashazos)
  // AppShellLayout contains CustomSider which calls useSession, causing cascading re-renders
  if (isLoading) {
    return <LoadingSpinner tip="" />;
  }

  const Component = <Redirector />;
  const element = (component: typeof Component) => (
    <AppShellLayout
      Sider={isLtiAnalyticsPage ? EmptyLayoutSlot : CustomSider}
      Header={isLtiAnalyticsPage ? EmptyLayoutSlot : StudioHeader}
    >
      <BackgroundImage>
        <ThemeProvider theme={theme}>
          <ErrorBoundary id={location.pathname}>{component}</ErrorBoundary>
        </ThemeProvider>
      </BackgroundImage>
    </AppShellLayout>
  );
  return (
    <>
      {element(Component)}
      <EnableMfaToast
        open={mfaSetupNudge.open}
        onEnableNow={mfaSetupNudge.onEnableNow}
        onSkip={mfaSetupNudge.onSkip}
      />
    </>
  );
};

// App initialization component - runs inside RefineWrapper
const AppInitializer = () => {
  useEffect(() => {
    initializeAnalytics();
    // Clean up any stale service workers to prevent issues with buggy code
    // Do not remove MSW however
    if ("scrollRestoration" in globalThis.history) {
      globalThis.history.scrollRestoration = "manual";
    }
  }, []);

  return null; // This component only runs side effects
};

// Create QueryClient instance for the app
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: (failureCount, error) => {
        // Retry once on 401 (token already refreshed by interceptor)
        const status =
          (error as { statusCode?: number } | undefined)?.statusCode ||
          (error as { status?: number } | undefined)?.status;
        return status === 401 && failureCount < 1;
      },
      retryDelay: 0,
      // Disable global refetch on window focus — only useGetTransferToken
      // enables it explicitly for token freshness.
      refetchOnWindowFocus: false,
      // Ensure queries execute immediately when enabled
      networkMode: "always",
      // Prevent aggressive garbage collection
      cacheTime: 1000 * 60 * 5, // 5 minutes
    },
  },
});

// CRITICAL: Setup authProvider with queryClient so logout can clear cache.
// Without this, queries persist in cache (gcTime: 10 min) after logout,
// causing the next user to see stale data from the previous user.
setupAuthProvider(queryClient);

function App() {
  // IdP-initiated SSO: Scalekit redirects to the app root (/) with idp_initiated_login param.
  // Must intercept here before Refine's auth guard redirects to /login and strips the param.
  const idpParam = new URLSearchParams(globalThis.location.search).get(
    "idp_initiated_login"
  );
  if (idpParam && !globalThis.location.pathname.includes("/auth/callback")) {
    globalThis.location.replace(
      `/auth/callback/scalekit?idp_initiated_login=${encodeURIComponent(idpParam)}`
    );
    return null;
  }

  return (
    <BrowserRouter
      future={{ v7_startTransition: true, v7_relativeSplatPath: true }}
    >
      <ScrollToTop />
      <ColorModeContextProvider>
        <AntApp>
          <QueryClientProvider client={queryClient}>
            <CookieConsentProvider>
              <AppContent />
            </CookieConsentProvider>
          </QueryClientProvider>
        </AntApp>
      </ColorModeContextProvider>
    </BrowserRouter>
  );
}

// Component that uses useSession - must be inside QueryClientProvider
function AppContent() {
  const {
    accesses,
    hasAccesses,
    currentOrganization: organization,
    user,
    isLoading,
  } = useSession();
  const hasRole = createHasRole(accesses);
  const { t, i18n } = useTranslation();

  // Memoize resources to recalculate when accesses or language change
  // Note: adminOnly items are filtered in the sider based on user context
  const resources = useMemo(
    () => getResources(hasRole, t),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [accesses, i18n.language]
  );

  // Memoize Refine props to prevent infinite re-renders
  const dataProvider = useMemo(
    () => ({
      default: myDataProvider,
    }),
    []
  );

  // Memoize Breadcrumbs element separately to prevent new references
  const breadcrumbElement = useMemo(() => <Breadcrumbs />, []);

  const options = useMemo(
    () => ({
      disableTelemetry: true,
      warnWhenUnsavedChanges: true,
      breadcrumb: breadcrumbElement,
      reactQuery: {
        clientConfig: queryClient, // Use module-level constant (stable reference)
      },
    }),
    [breadcrumbElement] // queryClient is module constant, doesn't need to be in deps
  );

  const accessControlProvider = useMemo(
    () => createAccessControlProvider(accesses, hasAccesses),
    [accesses, hasAccesses]
  );

  return (
    <RefineWrapper
      dataProvider={dataProvider}
      options={options}
      accessControlProvider={accessControlProvider}
      resources={resources}
      authProvider={authProvider}
      routerProvider={routerProvider}
      i18nProvider={i18nProvider}
    >
      <NetworkManager />
      {/* Don't initialize auth context until session data is loaded to prevent excessive re-renders */}
      {!isLoading && <AuthInitializer />}
      <WebSocketManager />
      {!isLoading && <ExportPollingManager />}
      {!isLoading && <LiveActivitySubscription />}
      {!isLoading && <PageViewTracker />}
      <AppInitializer />
      <SoftPaywallProvider>
        <Routes>
          {/* Full-screen routes without layout */}
          <Route
            path="media/projects/:id/heygenius/preview"
            element={
              <Authenticated key="heygenius-preview">
                <ProjectProvider>
                  <HeyGeniusPreviewPage />
                </ProjectProvider>
              </Authenticated>
            }
          />
          {/* New experience page. Its own focused layout (sidebar +
            Intercom + auth + error boundary, no admin styled-components
            theme) so the page can scope its own theme-v2 ConfigProvider.
            Optional :instanceId — omit to fall back to "default". */}
          <Route element={<ExperienceV2Layout />}>
            <Route
              path="media/projects/:projectId/experience-v2"
              element={
                <>
                  <HtmlPageTitle titleKey="experienceV2.htmlTitle" />
                  <ExperienceV2Page />
                </>
              }
            />
            <Route
              path="media/projects/:projectId/experience-v2/:instanceId"
              element={
                <>
                  <HtmlPageTitle titleKey="experienceV2.htmlTitle" />
                  <ExperienceV2Page />
                </>
              }
            />
            {/* Creator-mode edit page for an existing experience. Shares this
              layout so the rail/cards sit in the same chrome as the viewer. */}
            <Route
              path="media/projects/:projectId/experience-v2/:instanceId/edit"
              element={
                <>
                  <HtmlPageTitle titleKey="experienceEdit.htmlTitle" />
                  <ExperienceEditPage />
                </>
              }
            />
          </Route>
          {/* Playbook-centric "Create experience" flow (whitelisted rollout).
            Its own focused layout (sidebar + Intercom + auth + error boundary,
            no admin styled-components theme), mirroring experience-v2. */}
          <Route element={<CreateExperienceLayout />}>
            <Route
              path="create-experience"
              element={
                <>
                  <HtmlPageTitle titleKey="createExperience.htmlTitle" />
                  <CreateExperiencePage />
                </>
              }
            />
          </Route>
          <Route
            path="media/contacts/clone/mobile"
            element={<MobileCloningPage />}
          />
          {/* Auth pages outside Element - no sidebar/header layout, no isLoading gate */}
          <Route element={<SafeRedirectParam />}>
            <Route
              path="login"
              element={
                <>
                  <HtmlPageTitle titleKey="Login" />
                  <LoginPage />
                </>
              }
            />
            <Route
              path="verification"
              element={
                <>
                  <HtmlPageTitle titleKey="verification" />
                  <EmailVerificationPage />
                </>
              }
            />
            <Route
              path="register"
              element={
                <>
                  <HtmlPageTitle titleKey="register" />
                  <RegisterPage />
                </>
              }
            />
            <Route
              path="/auth/callback/google"
              element={
                <>
                  <HtmlPageTitle titleKey="googleOauthCallback" />
                  <OAuthCallbackPage />
                </>
              }
            />
            <Route
              path="/auth/callback/scalekit"
              element={
                <>
                  <HtmlPageTitle titleKey="scalekitOauthCallback" />
                  <ScalekitCallbackPage />
                </>
              }
            />
          </Route>
          <Route
            path="forgot-password"
            element={
              <>
                <HtmlPageTitle titleKey="forgotpassword" />
                <ForgotPasswordPage />
              </>
            }
          />
          <Route
            path="/mfa/verify"
            element={
              <>
                <HtmlPageTitle titleKey="mfaVerify" />
                <MfaVerifyPage />
              </>
            }
          />
          <Route
            path="/auth/logout"
            element={
              <>
                <HtmlPageTitle titleKey="logout" />
                <SsoLogoutPage />
              </>
            }
          />
          <Route
            path="/paywall"
            element={
              <Authenticated key="paywall" appendCurrentPathToQuery={false}>
                <HtmlPageTitle titleKey="paywall" />
                <PaywallPage />
              </Authenticated>
            }
          />
          <Route
            path="/questionnaire"
            element={
              <Authenticated
                key="questionnaire"
                appendCurrentPathToQuery={false}
              >
                <HtmlPageTitle titleKey="questionnaire" />
                <OnboardingQuestionnaire />
              </Authenticated>
            }
          />
          <Route
            path="/lti/deep-link"
            element={
              <Authenticated
                key="lti-deep-link"
                appendCurrentPathToQuery={false}
              >
                <LtiDeepLinkPage />
              </Authenticated>
            }
          />
          <Route
            path="/lti/session-expired"
            element={
              <>
                <HtmlPageTitle titleKey="ltiSessionExpired" />
                <LtiSessionExpiredPage />
              </>
            }
          />
          <Route
            path="/creator-profile"
            element={
              <Authenticated
                key="creator-profile"
                appendCurrentPathToQuery={false}
              >
                <TeacherOnboardingPage />
              </Authenticated>
            }
          />
          {/* Layout routes inside Element - sidebar/header + isLoading gate + Redirector */}
          <Route element={<Element />}>
            <Route index element={<Navigate to="home" />} />
            <Route path="/organization">
              <Route index element={<OrganizationPreferencesPage />} />
              <Route
                path="create"
                element={
                  <Authenticated
                    key="organization-create"
                    appendCurrentPathToQuery={false}
                  >
                    <CreateOrganizationPage />
                  </Authenticated>
                }
              />
            </Route>
            <Route path="/organizations">
              <Route path="create" element={<OrganizationCreatePage />} />
              <Route index element={<MediaOrganizationsList />} />
            </Route>
            <Route path="users" element={<UserEdit />} />
            <Route path="subscription">
              <Route index element={<SubscriptionPage />} />
              <Route
                path="success"
                element={
                  <>
                    <HtmlPageTitle titleKey="Pricing" />
                    <SubscriptionSuccess />
                  </>
                }
              />
              <Route path="members" element={<SubscriptionUsersPage />} />
              <Route path=":id/cancel" element={<SubscriptionCancel />} />
              <Route path=":id" element={<SubscriptionDetail />} />
              <Route path="subscriptions" element={<Subscriptions />} />
            </Route>
            <Route
              path="/manage-subscriptions"
              element={
                <CanAccess
                  action="show"
                  resource="media_generation_steps"
                  fallback={<Navigate to="/home" />}
                >
                  <HtmlPageTitle titleKey="manageSubscriptions" />
                  <ManageSubscriptionsPage />
                </CanAccess>
              }
            />
            <Route path="settings">
              <Route index element={<UserProfilePage />} />
            </Route>
            <Route path="tutorials" element={<TutorialsPage />} />
            <Route path="playbooks">
              <Route
                index
                element={
                  <>
                    <HtmlPageTitle titleKey="playbooks" />
                    <PlaybooksPage />
                  </>
                }
              />
              <Route
                path=":id/edit"
                element={
                  <>
                    <HtmlPageTitle titleKey="playbooks" />
                    <PlaybooksEditPage />
                  </>
                }
              />
            </Route>
            <Route path="system" element={<SystemPage />} />
            <Route path="/change-password" element={<ChangePassword />} />
            <Route
              path="extranet/boursorama"
              element={<BoursoramaVideoList />}
            />
            <Route path="/assets">
              <Route index element={<AssetOverview />} />
              <Route path="changes" element={<AssetChangesReview />} />
              <Route
                path="calendars"
                element={<AssetsEarningCalendarsPage />}
              />
              <Route path=":id" element={<AssetOverview />} />
            </Route>
            <Route
              path="home"
              element={
                <CanAccess
                  action="list"
                  resource="media_home"
                  fallback={<Navigate to="/login?to=%2Fhome" />}
                >
                  <HtmlPageTitle titleKey="home" />
                  <HomePage />
                </CanAccess>
              }
            />
            <Route
              path="lti/project/:id"
              element={
                <ProjectProvider>
                  <Detail />
                </ProjectProvider>
              }
            />
            <Route path="/workspaces">
              <Route
                index
                element={
                  <>
                    <HtmlPageTitle titleKey="workspaces" />
                    <WorkspacesListPage />
                  </>
                }
              />
              <Route path=":id/detail" element={<WorkspacesDetailPage />} />
            </Route>

            <Route path="/flags">
              <Route
                index
                element={
                  <>
                    <HtmlPageTitle titleKey="featureFlags" />
                    <FlagPage />
                  </>
                }
              />
              <Route path="create" element={<FlagCreatePage />} />
              <Route path=":id/edit" element={<FlagEditPage />} />
            </Route>

            <Route
              path="/whats-new"
              element={
                <>
                  <HtmlPageTitle titleKey="whatsNew" />
                  <WhatsNewPage />
                </>
              }
            />

            <Route path="/media">
              <Route index element={<MediaIndexPage />} />

              {/* for admin users */}
              <Route path="dashboard" element={<MediaDashboardPage />} />

              <Route path="media">
                <Route
                  index
                  element={
                    <>
                      <HtmlPageTitle titleKey="media" />
                      <MediaList />
                    </>
                  }
                />
                <Route path="create" element={<MediaCreate />} />
                <Route path=":id" element={<MediaDetail />} />
                <Route path=":id/edit" element={<MediaDetail />} />
              </Route>
              <Route path="batches">
                <Route
                  index
                  element={
                    <>
                      <HtmlPageTitle titleKey="batches" />
                      <MediaBatchList organization={organization} />
                    </>
                  }
                />
                <Route path=":id/:key" element={<MediaBatchList />} />
              </Route>
              <Route path="documents">
                <Route
                  index
                  element={<MediaDocumentList organization={organization} />}
                />
                <Route
                  path="upload"
                  element={<MediaDocumentCreate organization={organization} />}
                />
                <Route
                  path=":id"
                  element={<MediaDocumentDetail organization={organization} />}
                />
                <Route
                  path=":id/chat"
                  element={
                    <Chat
                      documentIds={["8d3efc14-9e60-44af-aaa0-000a2244ef9d"]}
                      organizationId="boursorama"
                    />
                  }
                ></Route>
              </Route>
              <Route
                path="assets"
                element={
                  <>
                    <HtmlPageTitle titleKey="Assets" />
                    <MediaAssetsList userId={user?.id} />
                  </>
                }
              />
              <Route
                path="assets/review"
                element={
                  <>
                    <HtmlPageTitle titleKey="assetsReview" />
                    <MediaAssetsReview />
                  </>
                }
              />
              <Route
                path="assets/review/stats"
                element={<MediaAssetsReviewStats />}
              />
              <Route path="assets/:id" element={<MediaAssetDetail />} />
              <Route
                path="assets/review/:sub_category"
                element={<MediaAssetsList userId={user?.id} />}
              />
              <Route
                path="analytics"
                element={
                  <>
                    <HtmlPageTitle titleKey="Analytics" />
                    <OrganizationAnalyticsPage />
                  </>
                }
              />
              <Route path="projects">
                <Route
                  index
                  element={
                    <>
                      <HtmlPageTitle titleKey="Projects" />
                      <ProjectsList />
                    </>
                  }
                />
                <Route path="create" element={<ProjectCreate />} />
                <Route
                  path="document"
                  element={
                    <CanAccess
                      action="list"
                      resource="media_home"
                      fallback={
                        <Navigate to="/login?to=%2Fmedia%2Fprojects%2Fdocument" />
                      }
                    >
                      <ProjectDocumentPage />
                    </CanAccess>
                  }
                />
                <Route
                  path="create/heygenius"
                  element={<HeyGeniusProjectCreatePage />}
                />
                <Route
                  path="prompt"
                  element={
                    <CanAccess
                      action="list"
                      resource="media_home"
                      fallback={
                        <Navigate to="/login?to=%2Fmedia%2Fprojects%2Fprompt" />
                      }
                    >
                      <ProjectInitPrompt />
                    </CanAccess>
                  }
                />
                <Route path="select-type" element={<ProjectSelectType />} />
                <Route path=":id">
                  {/* Single context instance per project
✅ Shared state across all project routes
✅ No context recreation when navigating between project routes */}
                  <Route
                    element={
                      <ProjectProvider>
                        <Outlet />
                      </ProjectProvider>
                    }
                  >
                    {/* Catch-all routes for project details */}
                    <Route index element={<Detail />} />
                    <Route path="all" element={<ProjectDetailAll />} />
                    <Route path="setup" element={<ProjectCreate />} />
                    <Route
                      path="setup/:setupType"
                      element={<ProjectCreate />}
                    />
                    <Route
                      path="analytics"
                      element={<AnalyticsDashboardPage />}
                    />
                    <Route
                      path="analytics/video-explainer"
                      element={<VideoExplainerAnalyticsPage />}
                    />
                    <Route
                      path="analytics/document-highlighter"
                      element={<DocumentHighlighterAnalyticsPage />}
                    />
                    <Route
                      path="analytics/ai-chatbot"
                      element={<AIChatbotAnalyticsPage />}
                    />
                    <Route
                      path="analytics/addons"
                      element={<AddonsAnalyticsRoute />}
                    />
                    <Route
                      path="analytics/activity"
                      element={<ActivityAnalyticsPage />}
                    />
                    <Route
                      path="chat-history"
                      element={<ProjectChatHistoryPage />}
                    />
                    <Route
                      path="script/generating"
                      element={
                        <>
                          <HtmlPageTitle titleKey="storyboardLoading" />
                          <ProjectStepLoadingPage waitingFor="storyboard" />
                        </>
                      }
                    />
                    <Route path="kb" element={<ProjectKbPage />} />
                    <Route
                      path="slides-streaming"
                      element={<SlidesStreaming />}
                    />
                    <Route
                      path="chat"
                      element={<AskDocs organization={organization} />}
                    />
                    <Route
                      path="heygenius/installation"
                      element={
                        <>
                          <HtmlPageTitle titleKey="heygeniusInstall" />
                          <HeyGeniusInstallPage />
                        </>
                      }
                    />
                    <Route path=":storyboardId">
                      <Route index element={<Detail />} />
                      <Route path="setup" element={<ProjectCreate />} />
                      <Route
                        path="setup/:setupType"
                        element={<ProjectCreate />}
                      />
                      <Route
                        path="settings/:setting"
                        element={<ProjectSettingsPage />}
                      />

                      <Route path=":instanceId">
                        <Route
                          path="settings/:setting"
                          element={<ProjectSettingsPage />}
                        />
                        <Route path="chat/edit" element={<EditChat />} />

                        <Route
                          path="script/generating"
                          element={
                            <>
                              <HtmlPageTitle titleKey="storyboardLoading" />
                              <ProjectStepLoadingPage waitingFor="storyboard" />
                            </>
                          }
                        />
                        <Route
                          path="script"
                          element={
                            <>
                              <HtmlPageTitle titleKey="scriptEditor" />
                              <ProjectStoryboardEditor />
                            </>
                          }
                        />
                        <Route path="storyboard">
                          <Route
                            path="editor"
                            element={
                              <>
                                <HtmlPageTitle titleKey="storyboardEditor" />
                                <TemplateDataProvider>
                                  <StoryboardEditor />
                                </TemplateDataProvider>
                              </>
                            }
                          />
                        </Route>
                      </Route>
                    </Route>
                    <Route path="storyboard/:storyboardId">
                      <Route
                        index
                        element={
                          <>
                            <HtmlPageTitle titleKey="storyboardEditor" />
                            <ProjectStoryboardEditor redirectLegacy={true} />
                          </>
                        }
                      />

                      <Route
                        path="video/loading"
                        element={
                          <>
                            <HtmlPageTitle titleKey="videoGeneration" />
                            <ProjectStepLoadingPage waitingFor="media" />
                          </>
                        }
                      />
                    </Route>
                    <Route path="media/:mediaId">
                      <Route
                        path="animation"
                        element={
                          <>
                            <HtmlPageTitle titleKey="highlightsEditor" />
                            <MediaCuepointsAnimation />
                          </>
                        }
                      />
                      <Route path="download" element={<DownloadMediaPage />} />
                    </Route>
                    <Route path="media/:mediaId">
                      <Route
                        path="animation"
                        element={
                          <>
                            <HtmlPageTitle titleKey="highlightsEditor" />
                            <MediaCuepointsAnimation />
                          </>
                        }
                      />
                      <Route path="download" element={<DownloadMediaPage />} />
                    </Route>
                  </Route>
                </Route>
              </Route>

              <Route path="voices">
                <Route
                  path="create"
                  element={
                    <>
                      <HtmlPageTitle titleKey="voicesCreate" />
                      <VoiceProfileCreatePage />
                    </>
                  }
                />
                <Route path=":id" element={<VoiceProfileDetailPage />} />
                <Route
                  index
                  element={
                    <>
                      <HtmlPageTitle titleKey="Voices" />
                      <VoiceProfilesListPage />
                    </>
                  }
                />
              </Route>
              <Route path="brandkits">
                <Route
                  index
                  element={
                    <>
                      <HtmlPageTitle titleKey="Brand Kits" />
                      <BrandKitList />
                    </>
                  }
                />

                <Route
                  path="create"
                  element={
                    <>
                      <HtmlPageTitle titleKey="brandKitsCreate" />
                      <BrandKitCreate />
                    </>
                  }
                />

                <Route
                  path=":id/edit"
                  element={
                    <>
                      <HtmlPageTitle titleKey="brandKitsEdit" />
                      <BrandKitEdit />
                    </>
                  }
                />

                <Route
                  path=":id"
                  element={
                    <>
                      <HtmlPageTitle titleKey="brandKits" />
                      <BrandKitEdit />
                    </>
                  }
                />
              </Route>
              <Route path="faces">
                <Route
                  path="create"
                  element={
                    <>
                      <HtmlPageTitle titleKey="facesCreate" />
                      <FaceProfileCreatePage />
                    </>
                  }
                />

                <Route
                  path=":id"
                  element={
                    <>
                      <HtmlPageTitle titleKey="faces" />
                      <FaceProfileDetailPage />
                    </>
                  }
                />

                <Route
                  index
                  element={
                    <>
                      <HtmlPageTitle titleKey="Faces" />
                      <FaceProfilesListPage />
                    </>
                  }
                />
              </Route>
              <Route path="presenters">
                <Route
                  index
                  element={
                    <>
                      <HtmlPageTitle titleKey="Presenters" />
                      <PresenterProfilesListPage />
                    </>
                  }
                />
              </Route>

              <Route path="contacts">
                <Route
                  path="create"
                  element={
                    <>
                      <HtmlPageTitle titleKey="contactsCreate" />
                      <AvatarEdit />
                    </>
                  }
                />
                <Route
                  path=":id"
                  element={
                    <>
                      <HtmlPageTitle titleKey="contactsEdit" />
                      <ContactEditPage />
                    </>
                  }
                />

                <Route
                  path=":id/edit"
                  element={
                    <>
                      <HtmlPageTitle titleKey="contactsEdit" />
                      <ContactEditPage />
                    </>
                  }
                />

                <Route
                  index
                  element={
                    <>
                      <HtmlPageTitle titleKey="Contacts" />
                      <ContactListPage />
                    </>
                  }
                />
              </Route>

              <Route path="ctas">
                <Route
                  path="create"
                  element={
                    <>
                      <HtmlPageTitle titleKey="callToActionsCreate" />
                      <CallToActionsFormPage />
                    </>
                  }
                />
                <Route path=":id" element={<CallToActionsFormPage />} />
                <Route
                  index
                  element={
                    <>
                      <HtmlPageTitle titleKey="CallToActions" />
                      <CallToActionsListPage />
                    </>
                  }
                />
              </Route>
              <Route
                path="pronunciations"
                element={
                  <>
                    <HtmlPageTitle titleKey="pronunciations" />
                    <PronunciationsPage />
                  </>
                }
              />

              <Route path="products">
                <Route
                  path="create"
                  element={
                    <>
                      <HtmlPageTitle titleKey="products - create" />
                      <ProductCreatePage />
                    </>
                  }
                />
                <Route
                  index
                  element={
                    <>
                      <HtmlPageTitle titleKey="Products" />
                      <ProductsList />
                    </>
                  }
                />
              </Route>
              <Route path="libraries">
                <Route index element={<AssetLibraries />} />
                <Route path="public" element={<AssetLibraries showPublic />} />
                <Route path="create" element={<CreateAssetLibraryPage />} />
                <Route
                  path="public/create"
                  element={<CreateAssetLibraryPage isPublic />}
                />
                <Route path=":id" element={<AssetLibraryEdit />} />
                <Route path=":id/edit" element={<AssetLibraryEdit isEdit />} />
              </Route>
            </Route>

            <Route
              path="invitations"
              element={
                <>
                  <HtmlPageTitle titleKey="invitations" />
                  <UserInvitesPage />
                </>
              }
            />

            <Route
              path="onboarding"
              element={
                <>
                  <HtmlPageTitle titleKey="Onboarding" />
                  <OnboardingPage />
                </>
              }
            />

            <Route
              path="theme-editor"
              element={
                <CanAccess
                  action="show"
                  resource="media_theme_editor"
                  fallback={<Navigate to="/home" />}
                >
                  <HtmlPageTitle titleKey="themeEditor" />
                  <Outlet />
                </CanAccess>
              }
            >
              <Route index element={<ThemeEditorPage />} />
              <Route path=":themeId" element={<ThemeEditorPage />} />
            </Route>

            <Route
              path="*"
              element={
                <>
                  <HtmlPageTitle titleKey="notFound" />
                  <NotFoundPage />
                </>
              }
            />
          </Route>
        </Routes>
      </SoftPaywallProvider>
      <ReactQueryDevtools initialIsOpen={false} />
    </RefineWrapper>
  );
}

export default App;
